import { getToolName, isToolUIPart } from 'ai';
import clsx from 'clsx';
import { CheckIcon, LoaderCircleIcon } from 'lucide-react';
import { type FC,useEffect, useRef } from 'react';

import { getMessageTextContent } from '../../../lib/ai.utils';
import type { UseAITaskState } from '../../hooks/useAiTask.hook';

const ToolMapper: Record<string, (args: Record<string, unknown>) => { label: string, details: string, link: string|null }> = {
    search: (args: Record<string, unknown>) => ({
        label: 'Vyhledávám',
        details: String(args['query'] ?? ''),
        link: null
    }),
    openPageAndExtract: (args: Record<string, unknown>) => ({
        label: 'Načítám stránku',
        details: String(args['url'] ?? ''),
        link: String(args['url'] ?? '')
    })
};

export interface AITaskStepsProps {
    task: UseAITaskState;
    className?: string;
}

export const AITaskSteps: FC<AITaskStepsProps> = ({
    task,
    className
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { messages, status, lastAssistantMessage } = task;

    const messageContent = getMessageTextContent(lastAssistantMessage);
    const wasFailure = messageContent.startsWith('Chyba: ');

    useEffect(() => {
        containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth'
        });
    }, [ messages ]);

    if (messages.length === 0) {
        return null;
    }

    return (
        <div ref={containerRef} className={clsx(
            'flex flex-col gap-[1px] w-full overflow-x-hidden overflow-y-auto',
            className
        )}>
            {messages
                .filter(m => m.role === 'assistant')
                .map((message, index) => (
                    <div key={index} className='flex flex-col gap-[1px] relative'>
                        {message.parts.map((part, partIndex) => {
                            // Check if this is a tool UI part
                            if (isToolUIPart(part)) {
                                const toolName = getToolName(part);
                                const toolInfo = ToolMapper[toolName]?.(part.input as Record<string, unknown> ?? {});

                                if (toolInfo) {
                                    return (
                                        <div key={partIndex} className='flex flex-row items-center gap-1 overflow-hidden text-sm text-gray-800 px-3 py-2 bg-gray-200'>
                                            {part.state === 'input-available' && (
                                                <LoaderCircleIcon size={16} className='flex-shrink-0 animate-spin text-gray-600' />
                                            )}
                                            {part.state === 'output-available' && (
                                                <CheckIcon size={16} className='flex-shrink-0 text-lime-600' />
                                            )}
                                            <span className='font-medium flex-shrink-0'>{toolInfo.label}:</span>
                                            <span className='flex-shrink overflow-hidden overflow-ellipsis text-nowrap'>
                                                {toolInfo.link
                                                    ? (
                                                        <a href={toolInfo.link} target='_blank' rel='noopener noreferrer' className='text-blue-500 hover:underline'>
                                                            {toolInfo.details}
                                                        </a>
                                                    )
                                                    : (
                                                        <span>{toolInfo?.details}</span>
                                                    )}
                                            </span>
                                        </div>   
                                    );
                                }
                            }
                        })}
                    </div>
                ))
            }

            {(status === 'streaming' || status === 'submitted') && (
                <div className='bg-gray-200 flex-grow' />
            )}

            {messageContent && (
                <div className={clsx(
                    'flex-grow flex-shrink-0 flex flex-col gap-1 overflow-hidden text-sm text-gray-800 px-3 py-3',
                    !wasFailure && 'bg-lime-200',
                    wasFailure && 'bg-orange-200'
                )}>
                    {!wasFailure && (
                        <span className='block text-xs'>Vygenerovaná citace:</span>
                    )}
                    <span className='font-semibold'>{messageContent}</span>
                </div>
            )}

            {status === 'error' && (
                <div className='flex-grow flex-shrink-0 flex flex-row items-center justify-center gap-1 overflow-hidden text-sm font-semibold text-gray-800 px-4 py-4 text-center bg-red-200'>
                    Při zpracování požadavku došlo k chybě. Zkuste to prosím znovu.
                </div>
            )}
        </div>
    );
};
